import {
  DomesticOrderConverterParams,
  LeisureMashup,
  PropertyMashup,
  BusMashup,
  TrainMashup,
  IDomesticOriginalOrder,
} from '~/api/routes/domestic/order/controller/type'
import { DateTime } from 'luxon'
import {
  TCouponSelectModalBusItem,
  TCouponSelectModalLeisureCouponRefetchInput,
  TCouponSelectModalLeisureItem,
  TCouponSelectModalPropertyCouponRefetchInput,
  TCouponSelectModalPropertyItem,
  TCouponSelectModalTrainItem,
} from '~/api/converters/domestic/order/Discount/CouponSelectModal/index.type'
import {
  getBusTicketDurationText,
  getBusTicketName,
  getBusTicketOptions,
  getSeatsTypeText,
} from '~/api/utils/bus'
import { isTruthy } from '~/api/utils/isTruthy'
import {
  badgeWithPolicyInOrder,
  getHasDayUseBadge,
} from '~/api/utils/accommodationBadgesWithPolicy'
import { getPreOrderRatePlanOptions } from '~/api/utils/domesticRateplan'
import {
  getDomesticTrainNoText,
  getDomesticTrainPassengersText,
  getDomesticTrainSeatDescription,
  getTrainTicketDurationText,
  getTrainTicketName,
  mergeDomesticTrainSeatText,
} from '~/api/utils/train'
import { getCouponSelectModalCouponPlatformList } from '~/api/converters/domestic/order/Discount/CouponSelectModal/couponPlatform'
import { getCouponSelectModalPropertyItemCoupons } from '~/api/converters/domestic/order/Discount/CouponSelectModal/propertyCoupons'
import { CouponPlatformInstanceAppId } from '~/api/services/coupon-platform/index.type'
import { getBadgeTypeWithProductEvent, getPreOrderLeisureValidityPeriod } from '~/api/utils/leisure'
import {
  getCapacityText,
  getTimeOptions,
} from '~/api/converters/domestic/order/ProductSection/Property/utils'

type Required = Pick<IDomesticOriginalOrder, 'couponSelectModal'>

// 쿠폰이 적용될 상품의 id
export const getCouponApplyItemId = (type, typeIdx) => {
  return `${type}:${typeIdx}`
}

const getPropertyCouponsInput = (
  property: PropertyMashup[number]
): TCouponSelectModalPropertyCouponRefetchInput => {
  const couponStayTypes = {
    R: 'REST',
    S: 'SLEEP',
    D: 'DAYS',
  }
  return {
    placeId: property.propertyId,
    price: property?.price ?? 0,
    stayType: couponStayTypes[property.useType],
    roomType: 'NORMAL',
    originalRoomPrice: property.ratePlan.totalOriginalRate,
    roomPrice: property?.price ?? 0,
    roomTypeId: property.roomType.id,
    checkinDateTime: property.checkInAt,
    checkoutDateTime: property.checkOutAt,
    typeIdx: property.typeIdx,
    useType: property.useType,
    checkInDate: property.checkInDate,
    ratePlanId: property.ratePlan.ratePlanId,
  }
}

export const getCouponSelectModalPropertyItem = (
  propertyMashup: PropertyMashup
): TCouponSelectModalPropertyItem[] => {
  return propertyMashup.map(property => {
    const badges = badgeWithPolicyInOrder({
      propertyBadges: property.config?.badges ?? [],
      optionBadges: property.roomType.config.badges ?? [],
      useType: property.useType,
      ratePlanBadges: property.ratePlan.config.badges ?? [],
    })

    const capacity = getCapacityText(property.roomType.capacity, property.roomType.maxCapacity)

    const roomTypeBadges = property.roomType.config?.badges ?? []
    const isDayUse = getHasDayUseBadge(roomTypeBadges)

    const timeOptions = getTimeOptions(property.checkInAt, property.checkOutAt, isDayUse)

    const coupons = getCouponSelectModalPropertyItemCoupons({
      property,
      coupons: property.coupons,
      typeIdx: property.typeIdx,
    })
    const additionalOneCoupons = getCouponSelectModalPropertyItemCoupons({
      property,
      coupons: property.additionalOneCoupons,
      typeIdx: property.typeIdx,
    })
    const additionalTwoCoupons = getCouponSelectModalPropertyItemCoupons({
      property,
      coupons: property.additionalTwoCoupons,
      typeIdx: property.typeIdx,
    })
    const couponApplyItemId = getCouponApplyItemId(property.type, property.typeIdx)
    const couponRefetchInput = getPropertyCouponsInput(property)

    // @deprecated
    const ratePlanBenefits = getPreOrderRatePlanOptions({
      benefitList: property.ratePlan.benefitList,
      config: property.ratePlan.config,
    }).join(' · ')
    const ratePlanBenefitList = getPreOrderRatePlanOptions({
      benefitList: property.ratePlan.benefitList,
      config: property.ratePlan.config,
    })

    return {
      badges,
      propertyName: property.name,
      roomTypeName: property.roomType.name,
      ratePlanTitle: property.ratePlan.title,
      ratePlanBenefits,
      ratePlanBenefitList,
      capacity,
      timeOptions,
      isDayUse,
      price: property.price ?? 0,
      coupons,
      additionalOneCoupons,
      additionalTwoCoupons,
      couponApplyItemId,
      couponRefetchInput,
    }
  })
}

const getLeisureCouponsInput = (
  leisure: LeisureMashup[number]
): TCouponSelectModalLeisureCouponRefetchInput => {
  return {
    price: leisure.price ?? 1000,
    items: [
      {
        productId: `${leisure.id}`,
        productType: leisure.productTypeCode,
      },
    ],
    typeIdx: leisure.typeIdx,
    id: leisure.id,
    appId: CouponPlatformInstanceAppId.Dl,
  }
}

export const getCouponSelectModalLeisureItem = (
  leisureMashup: LeisureMashup
): TCouponSelectModalLeisureItem[] => {
  const currentAt = DateTime.now().toISO()

  return leisureMashup.map(product => {
    const badges = getBadgeTypeWithProductEvent({ productEvent: product.productEvent, currentAt })
    const productName = product.productName
    const variant = product.leisureProductVariants[0]
    const optionGroupName = variant.productOptionGroup.productOptionGroupName
    const variantNameWithQuantity = `${variant.variantName} | ${variant.quantity}장`
    const validityPeriodText = getPreOrderLeisureValidityPeriod({
      validityStartDateTime: variant.validityStartDateTime,
      validityEndDateTime: variant.validityEndDateTime,
      currentAt,
    })
    const coupons = getCouponSelectModalCouponPlatformList({
      coupons: product.coupons,
      typeIdx: product.typeIdx,
      item: product,
    })
    const couponApplyItemId = getCouponApplyItemId(product.type, product.typeIdx)
    const couponRefetchInput = getLeisureCouponsInput(product)

    return {
      badges,
      productName,
      optionGroupName,
      variantNameWithQuantity,
      validityPeriodText,
      price: product.price ?? 0,
      coupons,
      additionalOneCoupons: [],
      additionalTwoCoupons: [],
      couponApplyItemId,
      couponRefetchInput,
    }
  })
}

export const getCouponSelectModalTrainItem = (
  trainMashup: TrainMashup
): TCouponSelectModalTrainItem[] => {
  return trainMashup.map(train => {
    const { adults, child, single, returns, coupons, typeIdx, type } = train
    const title = getTrainTicketName(train)

    const ways = [single, returns].filter(isTruthy).map(way => {
      const tripDurationText = getTrainTicketDurationText(way)
      const trainNoText = getDomesticTrainNoText({
        stationTrainClassifyName: way.stationTrainClassifyName,
        trainNo: way.trainNo,
      })
      const seatDescription = getDomesticTrainSeatDescription(way.seats)
      const passengersText = getDomesticTrainPassengersText({ adults, child })
      return {
        tripDurationText,
        seatsText: mergeDomesticTrainSeatText([trainNoText, seatDescription, passengersText]),
      }
    })

    const convertedCoupons = getCouponSelectModalCouponPlatformList({
      coupons,
      typeIdx,
      item: train,
    })

    const couponApplyItemId = getCouponApplyItemId(type, typeIdx)
    return {
      title,
      ways,
      price: train.price ?? 0,
      coupons: convertedCoupons,
      additionalOneCoupons: [],
      additionalTwoCoupons: [],
      couponApplyItemId,
    }
  })
}

export const getCouponSelectModalBusItem = (busMashup: BusMashup): TCouponSelectModalBusItem[] => {
  return busMashup.map(bus => {
    const { going, returns, available, coupons, typeIdx, type } = bus
    const title = getBusTicketName(bus)

    const ways = [going, returns].filter(isTruthy).map(way => {
      const tripDurationText = getBusTicketDurationText(way)

      const [busGradeText, seatsNoText] = getBusTicketOptions(way)
      const seatsTypeText = getSeatsTypeText(way.seats)

      return {
        tripDurationText: available ? tripDurationText : '',
        seatsText: `${busGradeText} | ${seatsNoText} | ${seatsTypeText}`,
      }
    })

    const convertedCoupons = getCouponSelectModalCouponPlatformList({ coupons, typeIdx, item: bus })
    const couponApplyItemId = getCouponApplyItemId(type, typeIdx)
    return {
      title,
      ways,
      price: bus.price ?? 0,
      coupons: convertedCoupons,
      additionalOneCoupons: [],
      additionalTwoCoupons: [],
      couponApplyItemId,
    }
  })
}

const getDomesticOrderDiscountCouponSelectModal = ({
  propertyMashup,
  leisureMashup,
  trainMashup,
  busMashup,
}: DomesticOrderConverterParams): Required => {
  const propertyItems = getCouponSelectModalPropertyItem(propertyMashup)
  const leisureItems = getCouponSelectModalLeisureItem(leisureMashup)
  const trainItems = getCouponSelectModalTrainItem(trainMashup)
  const busItems = getCouponSelectModalBusItem(busMashup)

  return {
    couponSelectModal: { propertyItems, leisureItems, trainItems, busItems },
  }
}

export default getDomesticOrderDiscountCouponSelectModal
